import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { DonorMetric } from "../../models/donor-metric.model";
import { NgbDateStruct, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NgModel } from "@angular/forms";
import { RecipientProfile } from "../../models/donorProfile.model";
import { ContentService } from "../../services/content.service";
import { EpicLookupModalComponent } from "../epic-lookup-modal/epic-lookup-modal.component";

@Component({
  selector: "app-profile-table",
  templateUrl: "./profile-table.component.html",
  styleUrls: ["./profile-table.component.scss"],
})
export class ProfileTableComponent implements OnInit, OnChanges {
  @Input() profileInfo: any;
  @Input() metrics: DonorMetric[] | null = null;
  @Input() showEditButton = false;
  @Input() redBasicInfo: string[] = [];
  @Input() yellowBasicInfo: string[] = [];
  @Input() isMrbView = false;
  @Output() epicUserSelected = new EventEmitter();

  isEditingRecipient = false;
  editableRecipient: any = {};
  isSavingRecipient = false;
  isRecipientDobInvalid = false;
  minDob: NgbDateStruct = { year: 1900, month: 1, day: 1 };
  maxDob: NgbDateStruct;

  bmiScaleConfig = {
    min: 15,
    max: 50,
    greenRange: [18.5, 24.9] as [number, number], // Normal BMI range
    yellowRange: [25, 29.9] as [number, number], // Overweight range
    redRange: [30, 50] as [number, number], // Obese range
    unit: "kg/m²",
  };

  bloodGroupOptions = ["A", "B", "AB", "O"];
  ethnicityOptions = [
    "White",
    "Asian",
    "African",
    "Black",
    "Hispanic or Latino",
    "Other",
  ];
  genderOptions = ["Male", "Female", "Other"];

  constructor(
    private contentService: ContentService,
    private modalService: NgbModal
  ) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate());
    this.maxDob = {
      year: tomorrow.getFullYear(),
      month: tomorrow.getMonth() + 1, // convert 0–11 -> 1–12
      day: tomorrow.getDate(),
    };
  }

  ngOnInit(): void {
    this.updateBMIfromMetrics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["metrics"] || changes["profileInfo"]) {
      this.updateBMIfromMetrics();
    }
  }

  private updateBMIfromMetrics(): void {
    // Set BMI from metrics if it's not coming back with profile info
    const bmiMetric = this.metrics?.find((m) => m.metric_key === "BMI");
    if (
      bmiMetric &&
      this.profileInfo &&
      this.profileInfo.data &&
      this.profileInfo.data.donor &&
      (!this.profileInfo.data.donor.bmi ||
        this.profileInfo.data.donor.bmi == "")
    ) {
      this.profileInfo.data.donor.bmi = bmiMetric.metric_value;
    }
  }

  calculateAge(dateOfBirth: string | null): string {
    if (!dateOfBirth) return "";

    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age.toString();
  }

  formatDateToMMDDYYYY(dateString: string): string {
    if (!dateString) return "";

    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }

  getRelationshipFromMetrics(): string {
    return this.profileInfo?.data?.donor?.relationshipToRecipient || "Unknown";
  }

  openEpicLookupModal(type: "Donor" | "Recipient") {
    const donorUserId = this.profileInfo?.data?.donor?.userId;

    const modalRef = this.modalService.open(EpicLookupModalComponent, {
      centered: true,
      backdrop: "static",
      size: "xl",
    });

    // Pass donorUserId as component input
    modalRef.componentInstance.donorUserId = donorUserId;
    modalRef.componentInstance.type = type;

    // Refresh users when modal closes
    modalRef.result
      .then((result) => {
        console.log(result);
        if (result.data.updatedFields.epicPatientId) {
          this.epicUserSelected.emit();
        }
      })
      .catch(() => {});
  }

  private formatNgbDate(date: NgbDateStruct | null | undefined): string | null {
    if (!date) return null;
    const { year, month, day } = date;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  private parseNgbDate(dateString: string): NgbDateStruct | null {
    if (!dateString) return null;
    const parts = dateString.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts.map((p) => parseInt(p, 10));
    if (!year || !month || !day) return null;
    return { year, month, day };
  }

  private buildRecipientPayload(src: any): RecipientProfile {
    const payload: any = { ...src };

    // Convert NgbDateStruct -> string for API, leave UI model untouched
    if (payload.dateOfBirth && typeof payload.dateOfBirth === "object") {
      payload.dateOfBirth = this.formatNgbDate(payload.dateOfBirth) || "";
    }

    // Normalize nulls to empty strings for all fields (API expectation)
    return Object.keys(payload).reduce((acc, key) => {
      const value = payload[key];
      (acc as any)[key] = value === null ? "" : value;
      return acc;
    }, {} as RecipientProfile);
  }

  sanitizeRecipient(data = null): RecipientProfile {
    // Kept for backwards compatibility; delegates to the new pure builder.
    const source = data ?? this.editableRecipient;
    return this.buildRecipientPayload(source);
  }

  onRecipientDobChange(date: NgbDateStruct | null, ctrl?: NgModel): void {
    // If the control is invalid due to parser/format errors, block save
    if (ctrl?.invalid && ctrl.errors?.["ngbDate"]?.invalid) {
      this.isRecipientDobInvalid = true;
      return;
    }

    // If empty, treat as valid (no date chosen)
    if (!date) {
      this.isRecipientDobInvalid = false;
      return;
    }

    // Enforce min/max range
    this.isRecipientDobInvalid =
      this.isBeforeMinDate(date) || this.isAfterMaxDate(date);
  }

  onEditRecipient() {
    const epicPatientId = this.profileInfo?.data?.recipient?.epicPatientId;
    if (!epicPatientId) {
      this.isEditingRecipient = true;
      this.editableRecipient = {
        ...this.profileInfo?.data?.recipient,
      } as RecipientProfile;
      if (
        this.profileInfo?.data?.recipient?.dateOfBirth &&
        typeof this.profileInfo.data.recipient.dateOfBirth === "string"
      ) {
        const parsed = this.parseNgbDate(
          this.profileInfo.data.recipient.dateOfBirth
        );
        if (parsed) {
          this.editableRecipient.dateOfBirth = parsed;
        }
      }
    } else {
      this.isEditingRecipient = true;
      this.editableRecipient = {
        donorUserId: this.profileInfo?.data?.recipient?.donorUserId,
        bloodGroup: this.profileInfo?.data?.recipient?.bloodGroup,
        causeOfEsrd: this.profileInfo?.data.recipient?.causeOfEsrd,
      } as RecipientProfile;
    }
  }

  onSaveRecipient() {
    this.isSavingRecipient = true;
    const payload: RecipientProfile = this.buildRecipientPayload(
      this.editableRecipient
    );

    this.contentService.updateRecipientProfileInfoByAdmin(payload).subscribe({
      next: (response) => {
        if (response.data.updatedFields) {
          const updatedRecipient = this.sanitizeRecipient(
            response.data.updatedFields
          );
          if (this.profileInfo) {
            this.profileInfo.data.recipient = { ...updatedRecipient };
          }
          this.epicUserSelected.emit();
        }
        this.isSavingRecipient = false;
        this.isEditingRecipient = false;
      },
      error: (err) => {
        this.isSavingRecipient = false;
        console.error("Failed to update recipient info", err);
      },
    });
  }

  onCancelRecipientEdit() {
    this.isEditingRecipient = false;
  }

  isBeforeMinDate(date: NgbDateStruct): boolean {
    if (!this.minDob || !date) return false;
    return (
      date.year < this.minDob.year ||
      (date.year === this.minDob.year && date.month < this.minDob.month) ||
      (date.year === this.minDob.year &&
        date.month === this.minDob.month &&
        date.day < this.minDob.day)
    );
  }

  isAfterMaxDate(date: NgbDateStruct): boolean {
    if (!this.maxDob || !date) return false;
    return (
      date.year > this.maxDob.year ||
      (date.year === this.maxDob.year && date.month > this.maxDob.month) ||
      (date.year === this.maxDob.year &&
        date.month === this.maxDob.month &&
        date.day > this.maxDob.day)
    );
  }
}
